<template>
  <v-container
    fluid
    class="pa-0"
  >
    <BaseHeader
      @filter="handleFilter"
      @filterButtonClick="handleFilterButtonClick"
    />
    <div class="position-relative">
      <!-- For larger screens, use row layout -->
      <div
        v-if="!isSmallScreen"
        class="d-flex flex-row ma-0 g-0"
      >
        <!-- Filter Dashboard Column -->
        <div
          v-if="showFilters"
          class="filter-col"
        >
          <div class="filter-wrapper">
            <FilterDashboard
              :class="['filter-dashboard']"
              v-model:show="showFilters"
              @filter-change="handleFilter"
              :topics="availableTopics"
            />
          </div>
        </div>

        <!-- Content Column for larger screens -->
        <div class="content-col">
          <div
            :class="contentClasses"
            ref="materialContainer"
          >
            <div
              v-if="isLoading && materials.length === 0"
              class="d-flex justify-center align-center empty-state-container"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              />
            </div>
            <v-alert
              v-else-if="queryError"
              type="error"
              class="empty-state-container mx-4"
              :text="
                retryCount >= 3
                  ? $t('ad-materials.messages.service-unavailable')
                  : $t('ad-materials.messages.error-loading')
              "
              variant="tonal"
              border="start"
              density="comfortable"
            >
              <template v-slot:append>
                <v-btn
                  v-if="retryCount < 3"
                  color="error"
                  variant="tonal"
                  class="font-weight-bold"
                  @click="handleRetry"
                >
                  {{ $t('ad-materials.actions.retry') }}
                </v-btn>
              </template>
            </v-alert>
            <v-alert
              v-else-if="!flattenedMaterials.length"
              type="info"
              class="empty-state-container mx-4"
              :text="$t('ad-materials.messages.no-materials')"
              variant="tonal"
              border="start"
              density="comfortable"
              icon="mdi-information-outline"
            >
              <template v-slot:prepend>
                <div class="d-flex align-center justify-center h-100">
                  <v-icon
                    color="info"
                    size="large"
                    >mdi-information-outline</v-icon
                  >
                </div>
              </template>
            </v-alert>

            <div
              v-else
              class="px-2 py-2 materials-container"
            >
              <v-row>
                <v-col
                  v-for="item in flattenedMaterials"
                  :key="item.id"
                  cols="12"
                  sm="6"
                  md="6"
                  lg="4"
                  xl="3"
                  class="pb-4"
                >
                  <MaterialCard
                    :name="item.displayName"
                    :url="item.fileUrl"
                    :topicId="showTopicId ? item.topicId : undefined"
                    :caption="item.topicName || ''"
                    :thumbnail-url="item.previewUrl"
                    :is-new="isNewMaterial(item.createdAt)"
                    :show-content-creator="!props.showTopicId"
                    :icon="item.icon"
                    class="h-100"
                  />
                </v-col>
              </v-row>

              <div
                v-if="isLoading && materials.length > 0"
                class="d-flex justify-center py-4 w-100"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                />
              </div>

              <!-- Invisible element for infinite scroll detection -->
              <div
                ref="scrollTrigger"
                class="scroll-trigger"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- For smaller screens, use overlay layout -->
      <div v-else>
        <!-- Filter Dashboard Overlay for small screens -->
        <div
          v-if="showFilters"
          class="filter-overlay"
        >
          <FilterDashboard
            :class="['filter-dashboard']"
            v-model:show="showFilters"
            @filter-change="handleFilter"
            :topics="availableTopics"
          />
        </div>

        <!-- Content for small screens -->
        <div
          :class="contentClasses"
          ref="materialContainer"
        >
          <div
            v-if="isLoading && materials.length === 0"
            class="d-flex justify-center align-center empty-state-container"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            />
          </div>
          <v-alert
            v-else-if="queryError"
            type="error"
            class="empty-state-container mx-4"
            :text="
              retryCount >= 3
                ? $t('ad-materials.messages.service-unavailable')
                : $t('ad-materials.messages.error-loading')
            "
            variant="tonal"
            border="start"
            density="comfortable"
          >
            <template v-slot:append>
              <v-btn
                v-if="retryCount < 3"
                color="error"
                variant="tonal"
                class="font-weight-bold"
                @click="handleRetry"
              >
                {{ $t('ad-materials.actions.retry') }}
              </v-btn>
            </template>
          </v-alert>
          <v-alert
            v-else-if="!flattenedMaterials.length"
            type="info"
            class="empty-state-container mx-4"
            variant="tonal"
            border="start"
            density="comfortable"
          >
            <template v-slot:prepend>
              <div class="d-flex align-center justify-center h-100">
                <v-icon
                  color="info"
                  size="large"
                  >mdi-information-outline</v-icon
                >
              </div>
            </template>
            <div class="d-flex align-center">
              {{ $t('ad-materials.messages.no-materials') }}
            </div>
          </v-alert>

          <div
            v-else
            class="px-2 py-2 materials-container"
          >
            <v-row>
              <v-col
                v-for="item in flattenedMaterials"
                :key="item.id"
                cols="12"
                sm="6"
                md="6"
                lg="4"
                xl="3"
                class="pb-4"
              >
                <MaterialCard
                  :name="item.displayName"
                  :url="item.fileUrl"
                  :topicId="showTopicId ? item.topicId : undefined"
                  :caption="item.topicName || ''"
                  :thumbnail-url="item.previewUrl"
                  :is-new="isNewMaterial(item.createdAt)"
                  :show-content-creator="!props.showTopicId"
                  :icon="item.icon"
                  class="h-100"
                />
              </v-col>
            </v-row>

            <div
              v-if="isLoading && materials.length > 0"
              class="d-flex justify-center py-4 w-100"
            >
              <v-progress-circular
                indeterminate
                color="primary"
              />
            </div>

            <!-- Invisible element for infinite scroll detection -->
            <div
              ref="scrollTrigger"
              class="scroll-trigger"
            />
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useQuery } from '@vue/apollo-composable'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import MaterialCard from '@/modules/landingpages/MaterialCard.vue'

// Remove SVG imports that are causing TypeScript errors
import AD_MATERIALS from './AdMaterials.gql'
import BaseHeader from './BaseHeader.vue'
import FilterDashboard from './FilterDashboard.vue'

interface File {
  url: string
  cdnUrl: string
}

interface MaterialFile {
  file: File
  previewFile?: File
}

interface Topic {
  id: string
  name: string
  landingpageId?: string
}

interface Material {
  id: string
  displayName: string
  files: MaterialFile[]
  landingpageId?: string
  topic?: Topic | null
  createdAt?: string
}

interface AdMaterialsQueryResult {
  adMaterials: {
    materials: Material[]
    topics: Topic[]
    meta: {
      total: number
      totalPages: number
    }
  }
}

interface Filters {
  target: string[]
  topics: string[]
  origin: string
  search: string
  sort: string
  assetType?: string
}

// Add this new interface for flattened materials
interface FlattenedMaterial {
  id: string
  displayName: string
  fileUrl: string
  previewUrl?: string
  topicId?: string
  topicName?: string
  createdAt?: string
  icon?: string
}

// Define file type enum for better type safety
type FileType = 'STATIC' | 'VIDEO' | 'IMAGE' | 'DOCUMENT'

// Props
const props = defineProps({
  showTopicId: {
    type: Boolean,
    default: false,
  },
})

// State
const materials = ref<Material[]>([])
const topics = ref<Topic[]>([])
const showFilters = ref(true)
const currentPage = ref(1)
const hasMorePages = ref(true)
const totalPages = ref(1)
const isLoading = ref(false)
const retryCount = ref(0)
const queryFailed = ref(false)
const scrollTrigger = ref<HTMLElement | null>(null)
const materialContainer = ref<HTMLElement | null>(null)
const isSmallScreen = ref(window.innerWidth <= 960)

// Watch for screen size changes using a more precise approach
watch(
  () => window.innerWidth,
  (width) => {
    if (width <= 960) {
      showFilters.value = false
    } else if (width > 960 && !showFilters.value) {
      showFilters.value = true
    }
  },
  { immediate: true }
)

// A single source of truth for all filters
const activeFilters = ref<Filters>({
  target: ['buyers', 'sellers'], // Default to both targets selected
  topics: [],
  origin: '', // Changed from origins array to origin string
  search: '',
  sort: 'new-to-old',
  assetType: '',
})

// Map frontend sort values to backend sortOrder values
const getSortOrder = (sort: string): string => {
  switch (sort) {
    case 'new-to-old':
      return 'new'
    case 'old-to-new':
      return 'old'
    default:
      return 'new'
  }
}

// Computed property for available topics - changed to return topic objects with id and name
const availableTopics = computed(() => {
  // Return unique topic names
  return [...new Set(topics.value.map((topic) => topic.name || ''))]
})

// Mapping function to convert topic names to IDs
const getTopicIdsByNames = (topicNames: string[]): string[] => {
  if (!topicNames.length || !topics.value.length) return []

  // Create a map of topic names to IDs for quick lookup
  const topicMap = topics.value.reduce(
    (map, topic) => {
      if (topic.name) {
        map[topic.name] = topic.id
      }
      return map
    },
    {} as Record<string, string>
  )

  // Convert names to IDs, filtering out any names that don't have a matching ID
  return topicNames.map((name) => topicMap[name]).filter((id) => id !== undefined)
}

// Query variables - computed only once and cached
const queryVariables = computed(() => {
  // Convert lowercase assetType to uppercase for GraphQL enum
  let assetTypeValue = undefined
  if (activeFilters.value.assetType) {
    assetTypeValue = activeFilters.value.assetType.toUpperCase()
  }

  // Convert topic names to IDs
  const topicIds = getTopicIdsByNames(activeFilters.value.topics)

  return {
    input: {
      page: currentPage.value,
      itemsPerPage: 20,
      buyers: activeFilters.value.target.includes('buyers'),
      sellers: activeFilters.value.target.includes('sellers'),
      custom: activeFilters.value.origin === 'custom',
      aiGenerated: activeFilters.value.origin === 'ai-generated',
      topicIds: topicIds,
      landingpageIds: [],
      search: activeFilters.value.search || '',
      sortOrder: getSortOrder(activeFilters.value.sort),
      assetTypeSearch: assetTypeValue,
    },
  }
})

// Apollo query with reactive variables
const {
  result,
  loading,
  error: queryError,
  refetch,
} = useQuery<AdMaterialsQueryResult>(AD_MATERIALS, () => queryVariables.value, {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-first',
})

// Track loading state from Apollo query
watch(loading, (newValue) => {
  isLoading.value = newValue
})

// Watch for query errors and update queryFailed state
watch(queryError, (error) => {
  queryFailed.value = !!error
})

// Update pagination info when query completes
watch(result, (data) => {
  if (data && data.adMaterials) {
    if (currentPage.value === 1) {
      // Reset materials for first page
      materials.value = data.adMaterials.materials || []
      // Set topics from query results
      topics.value = data.adMaterials.topics || []
    } else {
      // Append materials for subsequent pages
      materials.value = [...materials.value, ...(data.adMaterials.materials || [])]
    }

    // Update pagination info
    if (data.adMaterials.meta) {
      totalPages.value = data.adMaterials.meta.totalPages || 1
      hasMorePages.value = currentPage.value < totalPages.value
    } else {
      hasMorePages.value = false
    }

    // Reset error state on successful query
    if (queryFailed.value) {
      queryFailed.value = false
      retryCount.value = 0
    }
  }
})

// Update filters and force re-query when required
const updateFilters = (newFilters: Partial<Filters>): void => {
  // Reset to page 1 whenever filters change
  currentPage.value = 1

  // Reset error state when filters change
  if (queryFailed.value) {
    queryFailed.value = false
    retryCount.value = 0
  }

  // Update all filters at once to prevent cascading reactivity
  activeFilters.value = { ...activeFilters.value, ...newFilters }
}

// Define icons for different material types - use string paths instead of imports
const ICONS: Record<FileType, string> = {
  STATIC: '/img/material-static-preview.svg',
  VIDEO: '/img/material-video-preview.svg',
  IMAGE: '/img/material-static-preview.svg',
  DOCUMENT: '/img/material-static-preview.svg',
}

// Flatten materials and their files into a single array
const flattenedMaterials = computed(() => {
  if (!materials.value.length) return []

  const flattened: FlattenedMaterial[] = []

  materials.value.forEach((material) => {
    // Find matching topic for the material based on landingpageId
    const matchingTopic =
      topics.value.length > 0 && material.landingpageId
        ? topics.value.find((topic) => topic.landingpageId === material.landingpageId)
        : null

    // For each file in the material, create a flattened entry
    material.files.forEach((fileObj) => {
      // Skip null file objects
      if (!fileObj) return

      const { file, previewFile } = fileObj

      // Determine file type for icon selection (can be enhanced with more accurate type detection)
      const fileType = determineFileType(file.url)

      flattened.push({
        id: `${material.id}-${file.url}`,
        displayName: material.displayName,
        fileUrl: file.cdnUrl,
        previewUrl: previewFile?.cdnUrl,
        topicId: matchingTopic?.id,
        topicName: matchingTopic?.name,
        createdAt: material.createdAt,
        // Always provide an icon for fallback, even when thumbnail exists
        icon: ICONS[fileType],
      })
    })
  })

  // Sort by new status
  return flattened.sort((a, b) => {
    // First sort by new status
    const aIsNew = isNewMaterial(a.createdAt)
    const bIsNew = isNewMaterial(b.createdAt)

    if (aIsNew && !bIsNew) return -1
    if (!aIsNew && bIsNew) return 1

    // If both have the same new status, use the original sort order
    return 0
  })
})

// Helper function to determine file type from URL or extension
const determineFileType = (url: string): FileType => {
  if (!url) return 'STATIC'

  const lowerUrl = url.toLowerCase()

  if (lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.mov') || lowerUrl.includes('video')) {
    return 'VIDEO'
  } else if (
    lowerUrl.endsWith('.jpg') ||
    lowerUrl.endsWith('.jpeg') ||
    lowerUrl.endsWith('.png') ||
    lowerUrl.endsWith('.gif') ||
    lowerUrl.endsWith('.webp')
  ) {
    return 'IMAGE'
  } else if (
    lowerUrl.endsWith('.pdf') ||
    lowerUrl.endsWith('.doc') ||
    lowerUrl.endsWith('.docx') ||
    lowerUrl.endsWith('.txt')
  ) {
    return 'DOCUMENT'
  }

  return 'STATIC'
}

// Event handlers - all go through the single updateFilters function
const handleFilter = (filterData: Partial<Filters>): void => {
  updateFilters(filterData)
}

const handleFilterButtonClick = () => {
  showFilters.value = !showFilters.value
}

// Retry function for query errors
const handleRetry = async () => {
  retryCount.value++
  try {
    await refetch()
    if (!queryError.value) {
      // Reset retry count if successful
      retryCount.value = 0
      queryFailed.value = false
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'AdMaterials',
        action: 'handleRetry',
        retryCount: retryCount.value,
      },
      extra: {
        queryVariables: queryVariables.value,
      },
    })
  }
}

// Computed properties
const contentClasses = computed(() => ({
  'ads-materials-content': true,
  'flex-grow-1': true,
  'full-width': isSmallScreen.value,
  'ml-0': !showFilters.value || isSmallScreen.value,
  'ml-4': showFilters.value && !isSmallScreen.value,
  'transition-width': true,
}))

// Function to determine if a material is new (less than 30 days old)
const isNewMaterial = (createdAt?: string): boolean => {
  if (!createdAt) return false

  const createdDate = new Date(createdAt)
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

  return createdDate > thirtyDaysAgo
}

// Set up window scroll event for infinite scrolling and handle responsive behavior
onMounted(() => {
  // Add window resize event listener for more precise breakpoint control
  const handleResize = () => {
    const width = window.innerWidth
    isSmallScreen.value = width <= 960

    if (width <= 960) {
      showFilters.value = false
    } else if (width > 960) {
      showFilters.value = true
    }
  }

  // Initial check
  handleResize()

  // Add resize listener
  window.addEventListener('resize', handleResize)

  const checkForNewPage = () => {
    if (!scrollTrigger.value || !hasMorePages.value || isLoading.value || queryFailed.value) return

    const scrollTriggerRect = scrollTrigger.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight

    // Check if scroll trigger is visible in viewport (or about to be visible)
    if (scrollTriggerRect.top < viewportHeight + 300) {
      currentPage.value += 1
    }
  }

  // Add scroll event listener
  window.addEventListener('scroll', checkForNewPage)

  // Initial check in case content doesn't fill the page
  setTimeout(checkForNewPage, 500)

  // Clean up event listeners
  onUnmounted(() => {
    window.removeEventListener('scroll', checkForNewPage)
    window.removeEventListener('resize', handleResize)
  })
})
</script>

<style scoped>
.g-0 {
  --v-gutter-x: 0 !important;
}

.filter-col {
  width: 300px !important;
  min-width: 300px;
  max-width: 300px;
  box-sizing: border-box;
}

.content-col {
  flex: 1;
}

.filter-wrapper {
  position: sticky;
  top: 0;
  height: calc(100vh - 64px);
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  padding-right: 0;
  z-index: 10;
}

.filter-overlay {
  position: fixed;
  top: 64px;
  left: 0;
  height: calc(100vh - 64px);
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  width: 280px;
  z-index: 100;
}

.full-width {
  width: 100%;
}

@media (max-width: 1279px) {
  .filter-wrapper {
    top: 55px;
    height: calc(100vh - 120px);
  }

  .filter-overlay {
    top: 55px;
  }
}

@media (max-width: 959px) {
  .filter-col {
    width: 100% !important;
    min-width: 100%;
    max-width: 100%;
  }

  .filter-wrapper {
    position: relative;
    height: auto;
    top: 0;
    width: 100%;
  }

  .ads-materials-content {
    margin-left: 0;
    margin-top: 16px;
  }
}

/* Grid column sizing adjustments */
@media (min-width: 960px) and (max-width: 1263px) {
  .filter-col {
    width: 250px !important;
    min-width: 250px;
    max-width: 250px;
  }

  .filter-wrapper {
    width: 250px;
  }
}

.ads-materials-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 1px 0 8px rgba(0, 0, 0, 0.1);
  padding: 0px 12px;
  margin-left: 16px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.empty-state-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  margin: 16px 0;
}

:deep(.v-alert__prepend) {
  align-self: center !important;
}

.transition-width {
  transition: margin-left 0.3s ease;
}

.materials-container {
  position: relative;
}

.scroll-trigger {
  width: 100%;
  height: 10px;
  margin-top: 20px;
}
</style>
